import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { isAiComponentVisibleInAssets } from './aiComponentVerification';
import {
  isComponentsTabVisibleItem,
  isUserFacingAgent,
  matchesMcpComponentTypeFilter,
} from './isUserFacingAgent';
import {
  getMcpComponentAccessScope,
  getMcpComponentIdentityAccess,
  isMcpAssetComponentType,
} from './aiComponentMetadata';
import type { ComponentFilterState } from '../types';

export type AiAssetFilterSegment = 'agents' | 'components';

const MCP_MULTI_IDENTITY_ACCESS_MIN = 2;

export function hasMcpMultiIdentityAccess(
  components: AiComponentDto[],
): boolean {
  return components.some((component) => {
    if (
      !isComponentsTabVisibleItem({ component, isMcpApplication: true }) ||
      !isMcpAssetComponentType(component.type)
    ) {
      return false;
    }

    return (
      getMcpComponentIdentityAccess({ metadata: component.metadata }).length >=
      MCP_MULTI_IDENTITY_ACCESS_MIN
    );
  });
}

export function hasMcpAssetsWithSomeAccess(
  components: AiComponentDto[],
): boolean {
  if (!hasMcpMultiIdentityAccess(components)) {
    return false;
  }

  return components.some(
    (component) =>
      isComponentsTabVisibleItem({
        component,
        isMcpApplication: true,
        hideMcpProtocolMethods: true,
      }) &&
      isMcpAssetComponentType(component.type) &&
      getMcpComponentAccessScope({ metadata: component.metadata }) === 'SOME',
  );
}

export function filterAiAssetComponents({
  components,
  componentFilters,
  selectedFilter = 'components',
  isMcpApplication = false,
  includeSuppressed = false,
  hideMcpProtocolMethods = false,
}: {
  components: AiComponentDto[];
  componentFilters: ComponentFilterState;
  selectedFilter?: AiAssetFilterSegment;
  isMcpApplication?: boolean;
  includeSuppressed?: boolean;
  hideMcpProtocolMethods?: boolean;
}): AiComponentDto[] {
  if (selectedFilter === 'agents') {
    return components.filter((component) => {
      if (!isUserFacingAgent(component)) {
        return false;
      }

      return (
        !componentFilters.search ||
        component.name
          .toLowerCase()
          .includes(componentFilters.search.toLowerCase())
      );
    });
  }

  const accessFilter =
    isMcpApplication &&
    componentFilters.access &&
    hasMcpAssetsWithSomeAccess(components)
      ? componentFilters.access
      : '';

  return components.filter((component) => {
    const isComponent = isComponentsTabVisibleItem({
      component,
      isMcpApplication,
      hideMcpProtocolMethods,
    });
    const matchesSearch =
      !componentFilters.search ||
      component.name
        .toLowerCase()
        .includes(componentFilters.search.toLowerCase());
    const matchesType = isMcpApplication
      ? matchesMcpComponentTypeFilter({
          componentType: component.type,
          filterType: componentFilters.type,
          isMcpApplication: true,
        })
      : !componentFilters.type || component.type === componentFilters.type;
    const matchesCoverage =
      isMcpApplication ||
      !componentFilters.coverage ||
      (componentFilters.coverage === 'internal') === component.isInternal;
    const matchesRole =
      isMcpApplication ||
      !componentFilters.role ||
      component.role === componentFilters.role;
    const matchesMethod =
      !componentFilters.method ||
      component.interfaceType === componentFilters.method;
    const matchesAccess =
      !accessFilter ||
      !isMcpAssetComponentType(component.type) ||
      (getMcpComponentAccessScope({ metadata: component.metadata }) ??
        'ALL') === accessFilter;

    return (
      isComponent &&
      matchesSearch &&
      matchesType &&
      matchesCoverage &&
      matchesRole &&
      matchesMethod &&
      matchesAccess &&
      isAiComponentVisibleInAssets({
        component,
        codeCoverageFilter: componentFilters.codeCoverage,
        includeSuppressed,
      })
    );
  });
}

export function filterMcpAssetComponents({
  components,
  componentFilters,
  includeSuppressed = false,
  hideMcpProtocolMethods = false,
}: {
  components: AiComponentDto[];
  componentFilters: ComponentFilterState;
  includeSuppressed?: boolean;
  hideMcpProtocolMethods?: boolean;
}): AiComponentDto[] {
  return filterAiAssetComponents({
    components,
    componentFilters,
    selectedFilter: 'components',
    isMcpApplication: true,
    includeSuppressed,
    hideMcpProtocolMethods,
  });
}
